import React, {
  createContext,
  useMemo,
  PropsWithChildren,
  FC,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiltersForm } from '@shared/types/forms';
import { getTodayDate, toISOShortString } from '@utils/dates';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { decodeSearchParams, mapFilterToSearchQuery } from './utils';

export interface CaseDashboardProps {
  filters: FiltersForm;
  unsavedChanges: boolean;
  setUnsavedChanges: (value: boolean) => void;
  setFilters: (values: FiltersForm) => void;
  totalClaims?: number;
  setTotalClaimsCount?: (count: number) => void;
  handleClearFilterDates: () => void;
}

export const CaseDashboardContext = createContext<CaseDashboardProps>({
  filters: {},
  setFilters: () => undefined,
  totalClaims: undefined,
  unsavedChanges: false,
  setTotalClaimsCount: () => undefined,
  handleClearFilterDates: () => undefined,
  setUnsavedChanges: () => undefined,
});

export const CaseDashboardProvider: FC<PropsWithChildren> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<FiltersForm>(
    decodeSearchParams(searchParams) as FiltersForm
  );

  const [totalClaims, setTotalClaims] = useState<number>(0);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const {
    caseDetails: { claimSubmissionStartDate },
  } = useUserOrgCase();

  const caseStartDate = useMemo(
    () =>
      claimSubmissionStartDate
        ? toISOShortString(claimSubmissionStartDate)
        : undefined,
    [claimSubmissionStartDate]
  );
  const today = useMemo(() => toISOShortString(getTodayDate()), []);

  useEffect(() => {
    const activeFilters = {
      ...filters,
    };

    if (!filters.startDate || !filters.endDate) {
      activeFilters.startDate = caseStartDate;
      activeFilters.endDate = today;
    }

    setSearchParams(mapFilterToSearchQuery(activeFilters));
  }, [caseStartDate, filters, setSearchParams, today]);

  useEffect(() => {
    searchParams.forEach((value, key) => {
      if (key === 'orgId' || key === 'caseId') {
        setFilters((prevFilters) => ({
          ...prevFilters,
          [key]: value,
        }));
      }
    });
  }, [searchParams]);

  const handleClearFilterDates = useCallback(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      startDate: caseStartDate,
      endDate: today,
    }));
  }, [caseStartDate, today]);

  const setTotalClaimsCount = useCallback((count: number) => {
    setTotalClaims(count);
  }, []);

  const caseDashboardValues = useMemo(
    () => ({
      filters,
      setFilters,
      totalClaims,
      setTotalClaimsCount,
      handleClearFilterDates,
      setUnsavedChanges,
      unsavedChanges,
    }),
    [
      setUnsavedChanges,
      filters,
      totalClaims,
      setTotalClaimsCount,
      handleClearFilterDates,
      unsavedChanges,
    ]
  );

  return (
    <CaseDashboardContext.Provider value={caseDashboardValues}>
      {children}
    </CaseDashboardContext.Provider>
  );
};
